import { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';


const DEFAULT_CONFIG = {
  bgMode: 'color',
  bgColor: '#faf7f0',
  gradColor1: '#faf7f0',
  gradColor2: '#e8e0d0',
  gradDir: '135deg',
  bgImage: null,
  textColor: '#2a1f0e',
  accentColor: '#b8975a',
  fontFamily: "'Playfair Display', serif",
  orgName: 'Plataforma de Formación',
  headline: 'Certificate of Completion',
  certText: 'Certifica que',
  completedText: 'ha completado satisfactoriamente el módulo',
  signerName: 'Dirección Académica',
  signerLabel: 'Firma autorizada',
  sealText: 'CERT✦VALID',
  sealImage: null,
};

// A4 apaisado a 96 dpi
const W = 1123;
const H = 794;

// Convierte una URL de imagen en data URL para que html2canvas no la bloquee por CORS
const toDataUrl = async (url) => {
  if (!url || url.startsWith('data:')) return url;
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error('No se pudo cargar la imagen');
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
};

const buildBg = (c) => {
  if (c.bgMode === 'image' && c.bgImage) return `url(${c.bgImage}) center/cover no-repeat`;
  if (c.bgMode === 'gradient') return `linear-gradient(${c.gradDir}, ${c.gradColor1}, ${c.gradColor2})`;
  return c.bgColor || '#faf7f0';
};

const formatDate = (iso) =>
  new Date(iso || Date.now()).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

const slug = (s) =>
  (s || 'diploma')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

/* ── El certificado a tamaño real (se renderiza fuera de pantalla) ── */
const Certificate = ({ cfg, name, moduleTitle, date }) => {
  const accent = cfg.accentColor;
  const text = cfg.textColor;
  const font = cfg.fontFamily;

  return (
    <div
      style={{
        width: W, height: H, position: 'relative', boxSizing: 'border-box',
        background: buildBg(cfg), fontFamily: font, color: text,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '70px 90px',
      }}
    >
      {/* Marco y esquinas */}
      <div style={{ position: 'absolute', inset: 40, border: `1.5px solid ${accent}`, opacity: 0.7 }} />
      <div style={{ position: 'absolute', top: 24, left: 24, width: 80, height: 80, borderTop: `5px solid ${accent}`, borderLeft: `5px solid ${accent}` }} />
      <div style={{ position: 'absolute', bottom: 24, right: 24, width: 80, height: 80, borderBottom: `5px solid ${accent}`, borderRight: `5px solid ${accent}` }} />

      <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', color: accent }}>
        {cfg.orgName}
      </div>
      <div style={{ fontSize: 18, fontStyle: 'italic', opacity: 0.65, marginTop: 6, letterSpacing: '0.1em' }}>
        {cfg.headline}
      </div>
      <div style={{ width: 280, height: 2, margin: '18px 0', background: `linear-gradient(to right, transparent, ${accent}, transparent)` }} />

      <div style={{ fontSize: 14, letterSpacing: '0.18em', textTransform: 'uppercase', opacity: 0.65 }}>
        {cfg.certText}
      </div>
      <div style={{ fontSize: 58, fontWeight: 700, lineHeight: 1.15, margin: '10px 0', textAlign: 'center' }}>
        {name}
      </div>
      <div style={{ fontSize: 17, opacity: 0.65 }}>{cfg.completedText}</div>
      <div style={{ fontSize: 30, fontWeight: 700, fontStyle: 'italic', color: accent, textAlign: 'center', marginTop: 14, maxWidth: 800 }}>
        {moduleTitle}
      </div>
      <div style={{ width: 280, height: 2, margin: '18px 0 0', background: `linear-gradient(to right, transparent, ${accent}, transparent)` }} />

      {/* Pie: sello · firma · fecha */}
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          width: '100%', marginTop: 'auto', paddingTop: 16, borderTop: `1.5px solid ${accent}`,
        }}
      >
        <div
          style={{
            width: 96, height: 96, borderRadius: '50%', border: `2.5px solid ${accent}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0, color: accent,
            fontSize: 11, fontWeight: 800, textAlign: 'center', textTransform: 'uppercase', lineHeight: 1.4,
          }}
        >
          {cfg.sealImage ? (
            <img src={cfg.sealImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
          ) : (
            <span style={{ whiteSpace: 'pre-line' }}>
              {(cfg.sealText || 'CERT✦VALID').replaceAll('✦', '\n✦\n')}
            </span>
          )}
        </div>

        <div style={{ textAlign: 'center', flex: 1, padding: '0 20px' }}>
          <div style={{ fontSize: 22, fontStyle: 'italic' }}>{cfg.signerName}</div>
          <div style={{ width: 170, height: 1.5, background: accent, opacity: 0.5, margin: '6px auto' }} />
          <div style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', opacity: 0.55 }}>
            {cfg.signerLabel}
          </div>
        </div>

        <div style={{ textAlign: 'right', fontSize: 15, opacity: 0.75 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.6, marginBottom: 4 }}>
            Fecha de emisión
          </div>
          {date}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════ */
const DiplomaDownload = ({ module, user, api, refreshKey = 0, className = '' }) => {
  const [busy, setBusy] = useState(false);
  const [render, setRender] = useState(null);
  const [elig, setElig] = useState(null); // respuesta de /diploma-eligibility

  // El servidor decide si es elegible. Se vuelve a consultar cuando cambia refreshKey.
  useEffect(() => {
    if (!module?.id || !module?.diploma_enabled) { setElig(null); return; }
    let cancelled = false;
    api.get(`/modules/${module.id}/diploma-eligibility`)
      .then(({ data }) => { if (!cancelled) setElig(data); })
      .catch(() => { if (!cancelled) setElig(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module?.id, module?.diploma_enabled, refreshKey]);

  if (!elig?.eligible) return null;

  const handleDownload = async () => {
    setBusy(true);
    try {
      // 1. Diseño (el servidor vuelve a validar la elegibilidad)
      const { data } = await api.get(`/modules/${module.id}/diploma-config`);
      const cfg = { ...DEFAULT_CONFIG, ...(data || {}) };

      // 2. Imágenes a data URL
      const [bg, seal] = await Promise.all([
        toDataUrl(cfg.bgImage).catch(() => null),
        toDataUrl(cfg.sealImage).catch(() => null),
      ]);
      cfg.bgImage = bg;
      cfg.sealImage = seal;
      if (cfg.bgMode === 'image' && !bg) cfg.bgMode = 'color';

      // 3. Fecha REAL de finalización (viene del servidor)
      setRender({ cfg, date: formatDate(elig.completed_at) });
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (document.fonts?.ready) await document.fonts.ready;

      // 4. Captura → PDF
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
      const node = document.getElementById('diploma-render-target');
      const canvas = await html2canvas(node, {
        scale: 2, useCORS: true, backgroundColor: null, width: W, height: H,
      });

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [W, H], hotfixes: ['px_scaling'] });
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, W, H);
      pdf.save(`diploma_${slug(module.title)}.pdf`);

      toast.success('Diploma descargado');
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.status === 403
          ? 'Aún no cumples los requisitos del diploma.'
          : 'No se pudo generar el diploma. Inténtalo de nuevo.'
      );
    } finally {
      setRender(null);
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        className={className}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '10px 18px', borderRadius: 10, border: 'none',
          background: '#00FFFF', color: '#000', fontWeight: 800, fontSize: 12,
          letterSpacing: '0.06em', cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}
      >
        <Download size={14} />
        {busy ? 'Generando…' : 'Descargar diploma'}
      </button>

      {render && (
        <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden="true">
          <div id="diploma-render-target" style={{ width: W, height: H }}>
            <Certificate
              cfg={render.cfg}
              name={user?.name || ''}
              moduleTitle={module.title}
              date={render.date}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DiplomaDownload;